import React, { useState, useEffect, useRef } from 'react';
import { X, Gift, ArrowRight, Copy, Check, Sparkles, Shield, Clock, Users } from 'lucide-react';

interface ExitIntentPopupProps {
    onSignup?: () => void;
    couponCode?: string;
    discountText?: string;
}

const ExitIntentPopup: React.FC<ExitIntentPopupProps> = ({
    onSignup,
    couponCode = 'WELCOME10',
    discountText = '10% Off Your First Order'
}) => {
    const [isVisible, setIsVisible] = useState(false);
    const [copied, setCopied] = useState(false);
    const hasTriggered = useRef(false);
    const sessionKey = 'tc_exit_popup_shown';

    useEffect(() => {
        // Don't show if already shown in this session
        if (sessionStorage.getItem(sessionKey)) return;

        const handleMouseLeave = (e: MouseEvent) => {
            if (e.clientY <= 0 && !hasTriggered.current) {
                hasTriggered.current = true;
                sessionStorage.setItem(sessionKey, 'true');
                setIsVisible(true);
            }
        };

        // Also trigger on mobile if user scrolls up fast (back gesture)
        let lastScrollY = window.scrollY;
        const handleScroll = () => {
            const currentY = window.scrollY;
            const delta = lastScrollY - currentY;
            if (delta > 200 && currentY < 100 && !hasTriggered.current) {
                hasTriggered.current = true;
                sessionStorage.setItem(sessionKey, 'true');
                setIsVisible(true);
            }
            lastScrollY = currentY;
        };

        document.addEventListener('mouseleave', handleMouseLeave);
        window.addEventListener('scroll', handleScroll, { passive: true });

        return () => {
            document.removeEventListener('mouseleave', handleMouseLeave);
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    const handleCopy = () => {
        navigator.clipboard.writeText(couponCode);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    if (!isVisible) return null;

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/70 backdrop-blur-sm animate-fade-in"
                onClick={() => setIsVisible(false)}
            />

            {/* Popup */}
            <div className="relative bg-gradient-to-br from-gray-900 via-gray-900 to-gray-800 rounded-3xl max-w-lg w-full shadow-2xl shadow-orange-500/10 border border-gray-700/50 overflow-hidden animate-scale-in">
                {/* Close button */}
                <button
                    onClick={() => setIsVisible(false)}
                    className="absolute top-4 right-4 text-gray-500 hover:text-white transition-colors z-10"
                >
                    <X size={20} />
                </button>

                {/* Decorative gradient */}
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#ff5a0a] via-orange-400 to-yellow-400" />

                <div className="p-8 md:p-10 relative">
                    {/* Icon */}
                    <div className="flex justify-center mb-6">
                        <div className="relative">
                            <div className="w-20 h-20 bg-gradient-to-br from-[#ff5a0a] to-orange-600 rounded-2xl flex items-center justify-center shadow-xl shadow-orange-500/30 rotate-3">
                                <Gift size={36} className="text-white -rotate-3" />
                            </div>
                            <div className="absolute -top-2 -right-2 bg-green-500 text-white text-[9px] font-black uppercase tracking-wider px-2 py-1 rounded-lg shadow-lg animate-bounce">
                                Free!
                            </div>
                        </div>
                    </div>

                    {/* Headline */}
                    <div className="text-center mb-6">
                        <h2 className="text-2xl md:text-3xl font-black text-white mb-2 tracking-tight">
                            Wait! Don't Leave Empty-Handed
                        </h2>
                        <p className="text-gray-400 text-sm max-w-md mx-auto">
                            Get <span className="text-white font-bold">6,000 free visitors</span> to test our platform,
                            plus an exclusive discount code for your first purchase.
                        </p>
                    </div>

                    {/* Coupon Code */}
                    <div className="bg-gradient-to-r from-orange-500/10 to-yellow-500/10 border-2 border-dashed border-[#ff5a0a]/40 rounded-2xl p-5 mb-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <div className="text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-1">Exclusive Discount</div>
                                <div className="text-xl font-black text-white tracking-widest">{couponCode}</div>
                                <div className="text-xs text-[#ff5a0a] font-bold mt-0.5">{discountText}</div>
                            </div>
                            <button
                                onClick={handleCopy}
                                className={`flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wider transition-all ${copied
                                        ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                                        : 'bg-gray-800 text-white border border-gray-700 hover:bg-[#ff5a0a] hover:border-[#ff5a0a]'
                                    }`}
                            >
                                {copied ? <><Check size={14} /> Copied!</> : <><Copy size={14} /> Copy</>}
                            </button>
                        </div>
                    </div>

                    {/* Trust signals */}
                    <div className="grid grid-cols-3 gap-3 mb-6">
                        {[
                            { icon: <Shield size={14} />, text: 'Safe & Secure' },
                            { icon: <Clock size={14} />, text: '2-Min Setup' },
                            { icon: <Users size={14} />, text: '14K+ Users' },
                        ].map((item, i) => (
                            <div key={i} className="flex items-center gap-1.5 justify-center text-gray-400 text-[10px] font-bold uppercase tracking-wider">
                                <span className="text-[#ff5a0a]">{item.icon}</span>
                                {item.text}
                            </div>
                        ))}
                    </div>

                    {/* CTA */}
                    <button
                        onClick={() => {
                            setIsVisible(false);
                            onSignup?.();
                        }}
                        className="w-full bg-gradient-to-r from-[#ff5a0a] to-orange-500 text-white py-4 rounded-xl text-sm font-black uppercase tracking-widest hover:from-black hover:to-gray-900 transition-all shadow-xl shadow-orange-500/20 flex items-center justify-center gap-2 group"
                    >
                        <Sparkles size={16} />
                        Claim 2,000 Free Visitors
                        <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                    </button>

                    <p className="text-center text-[10px] text-gray-600 font-bold uppercase tracking-wider mt-3">
                        No credit card required · Cancel anytime
                    </p>
                </div>
            </div>

            <style>{`
                @keyframes fade-in { from { opacity: 0; } to { opacity: 1; } }
                @keyframes scale-in { from { opacity: 0; transform: scale(0.9) translateY(20px); } to { opacity: 1; transform: scale(1) translateY(0); } }
                .animate-fade-in { animation: fade-in 0.3s ease-out; }
                .animate-scale-in { animation: scale-in 0.4s cubic-bezier(0.16, 1, 0.3, 1); }
            `}</style>
        </div>
    );
};

export default ExitIntentPopup;
